import type { Tone } from '../components/ui/Badge.tsx';

export type Role = 'admin' | 'guest';

export interface Lead {
  id: number;
  full_name: string;
  practice_name: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  specialty: string | null;
  practice_type: string | null;
  city: string | null;
  state: string | null;
  timezone: string | null;
  source: string;
  source_detail: string | null;
  archetype: string;
  status: string;
  opted_in_at: string | null;
  first_touch_at: string | null;
  last_touch_at: string | null;
  do_not_contact: boolean;
  dq_reason: string | null;
  notes: string | null;
}

export interface LeadListRow extends Lead {
  current_stage: string | null;
  current_stage_at: string | null;
  deal_value: string | null;
}

export interface LeadDetail {
  lead: Lead;
  profile: Record<string, unknown> | null;
  currentStage: { stage: string; occurred_at: string; days_in_stage: number } | null;
  money: { contracted: string | null; collected: string };
  nextEvent: {
    call_id: number;
    next_event_owned: boolean | null;
    next_event_type: string | null;
    next_event_at: string | null;
    next_event_desc: string | null;
  } | null;
  counts: {
    calls: number;
    touches: number;
    deals: number;
    stage_events: number;
    contacts: number;
    payments: number;
  };
}

export type TimelineKindApi = 'call' | 'stage' | 'touch' | 'deal' | 'payment';

export interface TimelineEntry {
  kind: TimelineKindApi;
  id: number;
  occurred_at: string;
  data: Record<string, unknown>;
}

export interface PipelineCardApi {
  id: number;
  full_name: string;
  practice_name: string | null;
  source: string;
  deal_value: string | null;
  days_in_stage: number;
  stage_since: string;
  risk: 'none' | 'stalled' | 'at_risk';
  next_event_owned: boolean | null;
  next_event_desc: string | null;
  next_event_at: string | null;
}

export interface PipelineBoard {
  columns: { stage: string; label: string; cards: PipelineCardApi[] }[];
  totals: { stage: string; count: number; value: string }[];
}

/**
 * One place that decides how a stage reads, so the timeline, the board and
 * any badge all name and colour a stage identically.
 */
export const STAGE_LABEL: Record<string, string> = {
  lead_created: 'Lead created',
  call_booked: 'Call booked',
  call_ran: 'Call ran',
  details_sent_to_adam: 'Brief sent to Adam',
  assessment_sent: 'Assessment sent',
  assessment_reviewed: 'Assessment reviewed',
  booked_with_adam: 'Booked with Adam',
  verbal_yes: 'Verbal yes',
  contract_sent: 'Contract sent',
  contract_signed: 'Contract signed',
  first_payment: 'First payment',
  closed_won: 'Closed won',
  closed_lost: 'Closed lost',
  disqualified: 'Disqualified',
  nurture: 'Nurture',
  renewed: 'Renewed',
  churned: 'Churned',
};

export const STAGE_TONE: Record<string, Tone> = {
  lead_created: 'neutral',
  call_booked: 'open',
  call_ran: 'open',
  details_sent_to_adam: 'accent',
  assessment_sent: 'accent',
  assessment_reviewed: 'accent',
  booked_with_adam: 'accent',
  verbal_yes: 'won',
  contract_sent: 'accent',
  contract_signed: 'won',
  first_payment: 'won',
  closed_won: 'won',
  closed_lost: 'lost',
  disqualified: 'lost',
  nurture: 'stalled',
  renewed: 'won',
  churned: 'lost',
};

export const CALL_TYPE_LABEL: Record<string, string> = {
  discovery: 'Discovery call',
  assessment_review: 'Assessment review',
  kickoff: 'Kickoff',
  one_on_one: '1-on-1',
  renewal: 'Renewal call',
  crm_exploration: 'CRM exploration',
  other: 'Call',
};

export const CALL_STATUS_TONE: Record<string, Tone> = {
  ran: 'won',
  scheduled: 'open',
  no_show: 'lost',
  rescheduled: 'stalled',
  cancelled: 'lost',
};

export const LEAD_STATUS_TONE: Record<string, Tone> = {
  new: 'neutral',
  working: 'open',
  assessment: 'accent',
  with_adam: 'accent',
  won: 'won',
  lost: 'lost',
  nurture: 'stalled',
  disqualified: 'lost',
  client: 'won',
  churned: 'lost',
};

export function labelFor(map: Record<string, string>, key: string | null | undefined): string {
  if (!key) return '—';
  return map[key] ?? key.replace(/_/g, ' ');
}

/** `with_adam` becomes `With Adam`, for enum values shown raw in a badge. */
export function humanise(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
